import React, { useId } from "react";
import { DownOutlined } from "@ant-design/icons";
import styles from "./RegisterOrderForm.module.css";

type CollapsibleSectionProps = {
	title?: string;
	/** One line under the title saying what is inside, or what it is currently set to. */
	summary?: React.ReactNode;
	icon?: React.ReactNode;
	optionalTag?: string;
	/** Small pill after the title, e.g. how many of the fields inside are filled in. */
	badge?: React.ReactNode;
	open: boolean;
	onToggle: () => void;
	children: React.ReactNode;
};

/**
 * A group of optional fields that stays closed until it is needed.
 *
 * Controlled, so the page can open it when a rule inside it fails on save. The header keeps a
 * summary of what is inside, so a closed group still says what it holds.
 */
const CollapsibleSection = ({
	title,
	summary,
	icon,
	optionalTag,
	badge,
	open,
	onToggle,
	children,
}: CollapsibleSectionProps) => {
	const bodyId = useId();

	return (
		<section className={styles.collapsible}>
			<button
				type="button"
				className={styles.collapsibleHeader}
				aria-expanded={open}
				aria-controls={bodyId}
				onClick={onToggle}
			>
				{icon && (
					<span className={styles.collapsibleIcon} aria-hidden="true">
						{icon}
					</span>
				)}
				<span className={styles.collapsibleText}>
					<span className={styles.collapsibleTitle}>
						{title}
						{optionalTag && (
							<span className={styles.optionalTag}>
								{optionalTag}
							</span>
						)}
						{badge && (
							<span className={styles.collapsibleBadge}>
								{badge}
							</span>
						)}
					</span>
					{summary && (
						<span className={styles.collapsibleSummary}>
							{summary}
						</span>
					)}
				</span>
				<DownOutlined
					aria-hidden="true"
					className={`${styles.chevron} ${
						open ? styles.chevronOpen : ""
					}`}
				/>
			</button>
			{open && (
				<div id={bodyId} className={styles.collapsibleBody}>
					{children}
				</div>
			)}
		</section>
	);
};

export default CollapsibleSection;
